import api from './client';

const saveSessionAuth = (token, user) => {
  const isImpersonating = !!sessionStorage.getItem('authToken');
  const storage = isImpersonating ? sessionStorage : localStorage;

  if (token) {
    storage.setItem('authToken', token);
  }
  if (user) {
    let userObj = user;
    if (isImpersonating) {
      try {
        const prev = JSON.parse(sessionStorage.getItem('user') || '{}');
        userObj = {
          ...userObj,
          isImpersonated: prev.isImpersonated ?? true,
          impersonatedBy: prev.impersonatedBy ?? null,
        };
      } catch (_) {}
    }
    storage.setItem('user', JSON.stringify(userObj));
  }
};

export const portalService = {
  validateToken: async (token) => {
    try {
      const response = await api.post('/api/portal/validate-token', { token });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Validate portal token error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Link di accesso non valido o scaduto'
      };
    }
  },

  setup: async (token, loginEmail, password) => {
    try {
      const response = await api.post('/api/portal/setup', { token, loginEmail, password });
      if (response.data?.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { success: true, data: response.data };
      }
      return { success: false, message: response.data?.message || 'Errore durante la configurazione' };
    } catch (error) {
      console.error('Portal setup error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante la configurazione dell'account",
        requiresParentRegistration: !!error.response?.data?.requiresParentRegistration
      };
    }
  },

  registerParent: async (token, firstName, lastName, fiscalCode, phoneNumber, password) => {
    try {
      const response = await api.post('/api/portal/register-parent', {
        token,
        firstName,
        lastName,
        fiscalCode,
        phoneNumber,
        password
      });
      if (response.data?.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { success: true, data: response.data };
      }
      return { success: false, message: response.data?.message || 'Errore durante la registrazione del genitore' };
    } catch (error) {
      console.error('Portal register parent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore durante la registrazione del genitore'
      };
    }
  },

  tokenLogin: async (token) => {
    try {
      const response = await api.post('/api/portal/token-login', { token });
      if (response.data?.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { success: true, data: response.data };
      }
      return { success: false, message: response.data?.message || "Errore durante l'accesso" };
    } catch (error) {
      console.error('Portal token login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante l'accesso con token"
      };
    }
  },

  linkParent: async (token, password) => {
    try {
      const response = await api.post('/api/portal/link-parent', { token, password });
      if (response.data?.success) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { success: true, data: response.data };
      }
      return { success: false, message: response.data?.message || 'Errore durante il collegamento' };
    } catch (error) {
      console.error('Portal link parent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore durante il collegamento al genitore'
      };
    }
  },

  getLinkedProfiles: async () => {
    try {
      const response = await api.get('/api/portal/linked-profiles');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get linked profiles error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore nel recupero dei profili collegati'
      };
    }
  },

  switchProfile: async (targetUserId) => {
    try {
      const response = await api.post('/api/portal/switch-profile', { targetUserId });
      if (response.data?.success) {
        saveSessionAuth(response.data.token, response.data.user);
        return { success: true, data: response.data };
      }
      return { success: false, message: response.data?.message || 'Errore durante il cambio profilo' };
    } catch (error) {
      console.error('Switch profile error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore durante il cambio profilo'
      };
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/api/portal/profile');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Get portal profile error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore nel caricamento del profilo'
      };
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/api/portal/profile', profileData);
      if (response.data?.user) {
        const storage = sessionStorage.getItem('authToken') ? sessionStorage : localStorage;
        const storedUser = storage.getItem('user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            storage.setItem('user', JSON.stringify({ ...parsed, ...response.data.user }));
          } catch (e) {
            console.error('Error updating cached user:', e);
          }
        }
      }
      return { success: true, data: response.data, message: response.data?.message };
    } catch (error) {
      console.error('Update portal profile error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore nell'aggiornamento del profilo"
      };
    }
  },

  updateLoginEmail: async (loginEmail, targetUserId = null) => {
    try {
      const payload = { loginEmail, ...(targetUserId && { targetUserId }) };
      const response = await api.put('/api/portal/login-email', payload);

      const storage = sessionStorage.getItem('authToken') ? sessionStorage : localStorage;
      if (response.data?.token) {
        storage.setItem('authToken', response.data.token);
      }
      if (response.data?.user) {
        const storedUser = storage.getItem('user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            if (response.data.token || parsed.id === response.data.user.id) {
              storage.setItem('user', JSON.stringify({ ...parsed, ...response.data.user }));
            }
          } catch (e) {
            console.error('Error updating cached user:', e);
          }
        }
      }

      return {
        success: true,
        data: response.data,
        message: response.data?.message || 'Email di login aggiornata con successo'
      };
    } catch (error) {
      console.error('Update portal login email error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore nell'aggiornamento dell'email di login"
      };
    }
  },

  getParent: async () => {
    try {
      const response = await api.get('/api/portal/parent');
      return { success: true, data: response.data?.parent };
    } catch (error) {
      console.error('Get portal parent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore nel caricamento dei dati del genitore'
      };
    }
  },

  updateParent: async (parentData) => {
    try {
      const response = await api.put('/api/portal/parent', parentData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Update portal parent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore nel salvataggio dei dati del genitore'
      };
    }
  },

  getSections: async () => {
    try {
      const response = await api.get('/api/portal/sections');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get portal sections error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento delle sezioni informative'
      };
    }
  },

  getAttachments: async () => {
    try {
      const response = await api.get('/api/portal/attachments');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get portal attachments error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento degli allegati del portale'
      };
    }
  },

  createAttachment: async (formData) => {
    try {
      const response = await api.post('/api/portal/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Allegato creato con successo'
      };
    } catch (error) {
      console.error('Create portal attachment error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante il caricamento dell'allegato"
      };
    }
  },

  updateAttachment: async (id, formData) => {
    try {
      const response = await api.put(`/api/portal/attachments/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Allegato aggiornato con successo'
      };
    } catch (error) {
      console.error('Update portal attachment error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante l'aggiornamento dell'allegato"
      };
    }
  },

  deleteAttachment: async (id) => {
    try {
      const response = await api.delete(`/api/portal/attachments/${id}`);
      return {
        success: true,
        message: response.data?.message || 'Allegato eliminato con successo'
      };
    } catch (error) {
      console.error('Delete portal attachment error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante l'eliminazione dell'allegato"
      };
    }
  },

  downloadAttachment: async (attachment) => {
    try {
      const response = await api.get(`/api/portal/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.fileName || 'allegato';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
      } else {
        return { success: false, message: 'Errore durante il download del file' };
      }
    } catch (error) {
      console.error('Download portal attachment error:', error);
      return {
        success: false,
        message: 'Errore di connessione durante il download del file'
      };
    }
  },

  /**
   * Get public informational sections for registration
   */
  getPublicSections: async () => {
    try {
      const response = await api.get('/api/portal/public-sections');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get public portal sections error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento delle sezioni informative'
      };
    }
  },

  /**
   * Get public attachments for registration
   */
  getPublicAttachments: async () => {
    try {
      const response = await api.get('/api/portal/public-attachments');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get public portal attachments error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento degli allegati del portale'
      };
    }
  },

  /**
   * Get dynamic portal consents (for athlete or admin)
   */
  getConsents: async () => {
    try {
      const response = await api.get('/api/portal/consents');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get portal consents error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento dei consensi'
      };
    }
  },

  /**
   * Get dynamic consents and acceptance status for a specific user (admin)
   */
  getUserConsents: async (userId) => {
    try {
      const response = await api.get(`/api/portal/user-consents/${userId}`);
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get user consents error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento dei consensi utente'
      };
    }
  },

  /**
   * Create a new dynamic portal consent (admin)
   */
  createConsent: async (data) => {
    try {
      const response = await api.post('/api/portal/consents', data);
      return {
        success: true,
        data: response.data?.consent,
        message: response.data?.message || 'Consenso creato con successo'
      };
    } catch (error) {
      console.error('Create portal consent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Errore durante la creazione del consenso'
      };
    }
  },

  /**
   * Update a dynamic portal consent (admin)
   */
  updateConsent: async (id, data) => {
    try {
      const response = await api.put(`/api/portal/consents/${id}`, data);
      return {
        success: true,
        data: response.data?.consent,
        message: response.data?.message || 'Consenso aggiornato con successo'
      };
    } catch (error) {
      console.error('Update portal consent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante l'aggiornamento del consenso"
      };
    }
  },

  /**
   * Delete a dynamic portal consent (admin)
   */
  deleteConsent: async (id) => {
    try {
      const response = await api.delete(`/api/portal/consents/${id}`);
      return {
        success: true,
        message: response.data?.message || 'Consenso eliminato con successo'
      };
    } catch (error) {
      console.error('Delete portal consent error:', error);
      return {
        success: false,
        message: error.response?.data?.message || "Errore durante l'eliminazione del consenso"
      };
    }
  },

  /**
   * Get public consents for registration
   */
  getPublicConsents: async () => {
    try {
      const response = await api.get('/api/portal/public-consents');
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error('Get public portal consents error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Errore nel caricamento dei consensi'
      };
    }
  },

  /**
   * Download a public attachment during registration
   */
  downloadPublicAttachment: async (attachment) => {
    try {
      const response = await api.get(`/api/portal/public-attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.fileName || 'allegato';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          if (a.parentNode) {
            document.body.removeChild(a);
          }
        }, 1000);

        return { success: true };
      } else {
        return { success: false, message: 'Errore durante il download del file' };
      }
    } catch (error) {
      console.error('Download public portal attachment error:', error);
      return {
        success: false,
        message: 'Errore di connessione durante il download del file'
      };
    }
  }
};

export default portalService;
